/**
 * The vocabulary every chart in the kit speaks.
 *
 * One shape for every chart type, so a page can swap a bar for a line without
 * reshaping its data — and so the server can hand back something chart-ready
 * rather than rows the client has to pivot.
 */

/** One reading. */
export interface Datum {
  /** The category. A date key, a test id, an hour bucket — stable, not a label. */
  key: string;
  /** What a person reads on the axis and in the tooltip. */
  label: string;
  /**
   * The reading itself.
   *
   * `null` means **no data**, which is not zero. A day the lab was shut is a
   * gap in the line; a day it opened and did nothing is a point at zero. Charts
   * draw the first as a break and the second as a mark on the baseline.
   */
  value: number | null;
  /** Where clicking this datum goes. Omitted for a chart with no drill-down. */
  href?: string;
}

/**
 * A named run of readings.
 *
 * `tone` exists for the charts whose categories genuinely *are* statuses —
 * on-time green, breached red. Everything else takes its colour from its
 * position, so two charts on one screen agree about what "series 2" looks like.
 */
export interface Series {
  id: string;
  label: string;
  points: readonly Datum[];
  tone?: SeriesTone;
}

export type SeriesTone = 'success' | 'warning' | 'critical' | 'severe' | 'info' | 'amended';

export type Formatter = (value: number) => string;

export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** The drawable rectangle, after the axes have taken their margins. */
export interface PlotBox {
  width: number;
  height: number;
  margin: ChartMargin;
  /** Inner extent — what the scales map onto. */
  inner: { width: number; height: number };
}

export const DEFAULT_MARGIN: ChartMargin = { top: 8, right: 12, bottom: 28, left: 48 };

export function plotBox(width: number, height: number, margin: ChartMargin): PlotBox {
  return {
    width,
    height,
    margin,
    inner: {
      // Clamped at zero: a container narrower than its own axis labels would
      // otherwise produce a negative width and an SVG full of NaN.
      width: Math.max(0, width - margin.left - margin.right),
      height: Math.max(0, height - margin.top - margin.bottom),
    },
  };
}
